import { create } from 'zustand'

type TUploadType = 'FULL' | 'ADJUST'

type TVehicleType = 'CAR' | 'TRUCK' | 'MOTORBIKE' | 'TRACTOR' | 'BOAT'

type TVehicleFuel = '' | 'PETROL' | 'DIESEL' | 'OTHER'

type TNewUploadManualTuneRequired =
  | 'STOCK'
  | 'ECO'
  | 'BLEND'
  | 'STAGE1'
  | 'STAGE2'
  | 'STAGE3'

type TNewUploadVehicle = {
  id?: string
  make: string
  model: string
  engineSize: string
  hpModel?: string
  fuel: TVehicleFuel
  year: string
  startYear?: string | number | null
}

type TNewUploadTune = {
  ecuType: string
  tuningTool: string
  tuningToolOther?: string
}

type TNewUploadAnalysedFileInput = {
  vehicleType: TVehicleType
  vehicle: TNewUploadVehicle
  vrm?: string
  reference?: string
  mileage: string
  tune: TNewUploadTune
}

type TNewUploadAnalysedFileAvailableDTC = {
  description: string
  displayName: string
  status: string
}

type TNewUploadAnalysedFileAvailableMod = {
  automatic?: 0 | 1
  id?: number
  modCode?: string
  modDesc?: string
  modName?: string
  modStatus?: number
}

type TNewUploadAnalysedFileAuto = {
  availableDTCCount?: number
  availableDTCs: TNewUploadAnalysedFileAvailableDTC[]
  availableModCount?: number
  availableMods: TNewUploadAnalysedFileAvailableMod[]
  removedDTCs: string[]
  selectedMods: number[]
}

type TNewUploadAnalysedFileDtcOnlyPayment = {
  amount?: number
  consumedAt?: string
  paymentIntentId?: string
  required: boolean
  status: 'FAILED' | 'PENDING' | 'SUCCEEDED'
  totalAmount?: number
  vatAmount?: number
}

type TNewUploadAnalysedFileFile = {
  bucket: string
  id: string
  key: string
  name: string
  size: string
}

type TNewUploadOriginalUploadVehicle = {
  id?: string
  make: string
  model: string
}

type TNewUploadOriginalUpload = {
  id: string
  createdAt: string
  manualVehicle?: TNewUploadOriginalUploadVehicle
  reference?: string
  uploadNumber?: number
  uploadType: TUploadType
  vehicle?: TNewUploadOriginalUploadVehicle
  vehicleType: TVehicleType
  vrm?: string
}

type TNewUploadAnalysedFileManual = {
  fileOptions: TNewUploadManualFileOptions
  files: TNewUploadAnalysedFileFile[]
  originalUpload?: TNewUploadOriginalUpload
  tuneRequired: TNewUploadManualTuneRequired
  tuneRequiredCustom?: string
  uploadType?: TUploadType
}

type TNewUploadAnalysedFile = {
  auto?: TNewUploadAnalysedFileAuto
  createdAt: string
  dtcOnlyPayment?: TNewUploadAnalysedFileDtcOnlyPayment
  file: TNewUploadAnalysedFileFile
  id: string
  input: TNewUploadAnalysedFileInput
  manual?: TNewUploadAnalysedFileManual
  processingStatus?: 'PROCESSING' | 'READY' | 'FAILED'
  updatedAt: string
}

type TNewUploadFile = {
  id: string | null
  name: string
  size: number
  fileBlob: File | null
  isUploading: boolean
  uploadProgress: number | null
  isUploaded: boolean
  isAccepted: boolean
  isRejected: boolean
  rejectReason: string | null
  uploadErr: Error | null
  uploadUrl: string | null
  isPreparing: boolean | null
}

type TNewUploadInputFile = File & {
  rejectReason?: string
}

type TNewUploadManualFileOptions = {
  dpf: boolean
  dpfPCodes: string
  egr: boolean
  egrPCodes: string
  o2: boolean
  o2PCodes: string
  ppf: boolean
  ppfPCodes: string
  swirlFlap: boolean
  swirlFlapPCodes: string
  hardcut: boolean
  hardcutRpm: string
  adblue: boolean
  adbluePCodes: string
  egt: boolean
  egtPCodes: string
  vmax: boolean
  revLimiter: boolean
  immoOff: boolean
  popsAndBangs: boolean
  staticRevLimiterRaised: boolean
  deepIdle: boolean
  smokeMap: boolean
}

type TNewUploadCheckoutWarranty = {
  customerEmail: string
  customerName: string
  required: boolean
  type: 'WARRANTY_12' | 'WARRANTY_24'
}

type TNewUploadState = {
  uploadType: TUploadType
  vehicleType: TVehicleType
  vehicle: TNewUploadVehicle
  vrm: string | null
  reference: string
  mileage: string
  tune: TNewUploadTune
  file: TNewUploadFile | null
  manual: {
    files: TNewUploadFile[]
    fileOptions: TNewUploadManualFileOptions
    originalUpload: TNewUploadOriginalUpload | null
    tuneRequired: TNewUploadManualTuneRequired
    tuneRequiredCustom: string
    uploadType: TUploadType
  }
  build: {
    egtDisclaimerAgreed: boolean
    isSaving: boolean
    nextDisabled: boolean
  }
  analyse: {
    isSubmitting: boolean
  }
  checkout: {
    chosenPaymentMethodIndex: number
    createUploadDisabled: boolean
    creditsToPurchase: number | null
    ecuWarranty: boolean
    isCreating: boolean
    manualBuildUrl: string
    priority: boolean
    submitError: string
    warranty: TNewUploadCheckoutWarranty
  }
  analysedFile: TNewUploadAnalysedFile | null
  isNexusAvailable: boolean
  manualRequested: boolean
}

type TNewUploadActions = {
  setState: (updates: Partial<TNewUploadState>) => void
  addMainFile: (files: TNewUploadInputFile[]) => void
  removeMainFile: () => void
  addManualFiles: (files: TNewUploadInputFile[]) => void
  removeManualFile: (index: number) => void
  reset: () => void
}

type TNewUploadStore = TNewUploadState & TNewUploadActions

const initialVehicle: TNewUploadVehicle = {
  make: '',
  model: '',
  engineSize: '',
  hpModel: '',
  fuel: '',
  year: '',
}

const initialManualFileOptions: TNewUploadManualFileOptions = {
  dpf: false,
  dpfPCodes: '',
  egr: false,
  egrPCodes: '',
  o2: false,
  o2PCodes: '',
  ppf: false,
  ppfPCodes: '',
  swirlFlap: false,
  swirlFlapPCodes: '',
  hardcut: false,
  hardcutRpm: '',
  adblue: false,
  adbluePCodes: '',
  egt: false,
  egtPCodes: '',
  vmax: false,
  revLimiter: false,
  immoOff: false,
  popsAndBangs: false,
  staticRevLimiterRaised: false,
  deepIdle: false,
  smokeMap: false,
}

const initialState: TNewUploadState = {
  uploadType: 'FULL',
  vehicleType: 'CAR',
  vehicle: initialVehicle,
  vrm: null,
  reference: '',
  mileage: '',
  tune: {
    ecuType: '',
    tuningTool: '',
    tuningToolOther: '',
  },
  file: null,
  manual: {
    files: [],
    fileOptions: initialManualFileOptions,
    originalUpload: null,
    tuneRequired: 'STAGE1',
    tuneRequiredCustom: '',
    uploadType: 'FULL',
  },
  build: {
    egtDisclaimerAgreed: false,
    isSaving: false,
    nextDisabled: false,
  },
  analyse: {
    isSubmitting: false,
  },
  checkout: {
    chosenPaymentMethodIndex: 0,
    createUploadDisabled: false,
    creditsToPurchase: null,
    ecuWarranty: true,
    isCreating: false,
    manualBuildUrl: '',
    priority: false,
    submitError: '',
    warranty: {
      customerEmail: '',
      customerName: '',
      required: false,
      type: 'WARRANTY_12',
    },
  },
  analysedFile: null,
  isNexusAvailable: true,
  manualRequested: false,
}

const MAX_FILE_SIZE = 1024 * 1024 * 25

const getStateFromAnalysedFile = (
  analysedFile: TNewUploadAnalysedFile,
): Partial<TNewUploadState> => ({
  vehicleType: analysedFile.input.vehicleType,
  vehicle: analysedFile.input.vehicle,
  vrm: analysedFile.input.vrm || null,
  reference: analysedFile.input.reference || '',
  mileage: analysedFile.input.mileage,
  tune: analysedFile.input.tune,
  file: {
    id: analysedFile.file.id,
    name: analysedFile.file.name,
    size: parseInt(`${analysedFile.file.size}`, 10) || 0,
    fileBlob: null,
    isUploading: false,
    uploadProgress: 100,
    isUploaded: true,
    isAccepted: true,
    isRejected: false,
    rejectReason: null,
    uploadErr: null,
    uploadUrl: null,
    isPreparing: false,
  },
  manual: {
    files:
      analysedFile.manual?.files.map((file): TNewUploadFile => {
        return {
          id: file.id,
          name: file.name,
          size: parseInt(`${file.size}`, 10) || 0,
          fileBlob: null,
          isUploading: false,
          uploadProgress: 100,
          isUploaded: true,
          isAccepted: true,
          isRejected: false,
          rejectReason: null,
          uploadErr: null,
          uploadUrl: null,
          isPreparing: false,
        }
      }) || [],
    fileOptions: {
      ...initialManualFileOptions,
      ...analysedFile.manual?.fileOptions,
    },
    originalUpload: analysedFile.manual?.originalUpload || null,
    tuneRequired: analysedFile.manual?.tuneRequired || 'STAGE1',
    tuneRequiredCustom: analysedFile.manual?.tuneRequiredCustom || '',
    uploadType: analysedFile.manual?.uploadType || 'FULL',
  },
  build: initialState.build,
  analyse: initialState.analyse,
  checkout: initialState.checkout,
  analysedFile,
  manualRequested: Boolean(analysedFile.manual),
})

const mapUploadFile = (file: TNewUploadInputFile): TNewUploadFile => {
  const isTooLarge = file.size > MAX_FILE_SIZE
  const rejectReason = file.rejectReason
    ? file.rejectReason
    : isTooLarge
      ? 'File size is too large'
      : null

  return {
    id: null,
    name: file.name,
    size: file.size,
    fileBlob: file,
    isUploading: false,
    uploadProgress: null,
    isUploaded: false,
    isAccepted: !rejectReason,
    isRejected: Boolean(rejectReason),
    rejectReason,
    uploadErr: null,
    uploadUrl: null,
    isPreparing: null,
  }
}

const useNewUpload = create<TNewUploadStore>()((set) => ({
  ...initialState,
  setState: (updates) => set(updates),
  addMainFile: (files) => {
    const file = files[0]

    if (!file) {
      return
    }

    set({ file: mapUploadFile(file) })
  },
  removeMainFile: () => set({ file: null }),
  addManualFiles: (files) => {
    if (!files.length) {
      return
    }

    set((state) => ({
      manual: {
        ...state.manual,
        files: [...state.manual.files, ...files.map(mapUploadFile)],
      },
    }))
  },
  removeManualFile: (index) =>
    set((state) => ({
      manual: {
        ...state.manual,
        files: state.manual.files.filter((_, fileIndex) => fileIndex !== index),
      },
    })),
  reset: () => set(initialState),
}))

export type {
  TNewUploadActions,
  TNewUploadAnalysedFile,
  TNewUploadAnalysedFileInput,
  TNewUploadAnalysedFileAuto,
  TNewUploadAnalysedFileAvailableDTC,
  TNewUploadAnalysedFileAvailableMod,
  TNewUploadAnalysedFileFile,
  TNewUploadAnalysedFileManual,
  TNewUploadManualFileOptions,
  TNewUploadManualTuneRequired,
  TNewUploadCheckoutWarranty,
  TNewUploadOriginalUpload,
  TNewUploadState,
  TNewUploadStore,
  TNewUploadFile,
  TNewUploadInputFile,
  TNewUploadTune,
  TNewUploadVehicle,
  TUploadType,
  TVehicleFuel,
  TVehicleType,
}

export { getStateFromAnalysedFile }

export default useNewUpload
